import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Pagination,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { Add, Delete, Edit, Search } from "@mui/icons-material";

import api from "../../../services/api";

const Division = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // =========================================================
  // STATE
  // =========================================================

  const [divisions, setDivisions] = useState([]);
  const [railways, setRailways] = useState([]);

  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);

  const rowsPerPage = 10;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [form, setForm] = useState({
    code: "",
    name: "",
    railwayId: "",
    status: "Active",
  });

  // =========================================================
  // MESSAGE
  // =========================================================

  const showMessage = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // =========================================================
  // LOAD RAILWAYS
  // =========================================================

  const loadRailways = async () => {
    try {
      const response = await api.get("/railways");

      if (response.data.success) {
        setRailways(response.data.data || []);
      } else {
        showMessage(
          response.data.message || "Unable to load railways",
          "error"
        );
      }
    } catch (error) {
      console.error("Load railways:", error);

      showMessage(
        error.response?.data?.message ||
          "Unable to load railways",
        "error"
      );
    }
  };

  // =========================================================
  // LOAD DIVISIONS
  // =========================================================

  const loadDivisions = async () => {
    try {
      const response = await api.get("/divisions");

      if (response.data.success) {
        setDivisions(response.data.data || []);
      } else {
        showMessage(
          response.data.message || "Unable to load divisions",
          "error"
        );
      }
    } catch (error) {
      console.error("Load divisions:", error);

      showMessage(
        error.response?.data?.message ||
          "Unable to load divisions",
        "error"
      );
    }
  };

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      try {
        await Promise.all([
          loadRailways(),
          loadDivisions(),
        ]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // =========================================================
  // FILTER
  // =========================================================

  const filteredData = useMemo(() => {
    const value = search.trim().toLowerCase();

    if (!value) {
      return divisions;
    }

    return divisions.filter((item) =>
      Object.values(item).some((field) =>
        String(field ?? "")
          .toLowerCase()
          .includes(value)
      )
    );
  }, [divisions, search]);

  // =========================================================
  // TOTAL PAGES
  // =========================================================

  const totalPages = Math.ceil(
    filteredData.length / rowsPerPage
  );

  // =========================================================
  // PAGINATED DATA
  // =========================================================

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;

    return filteredData.slice(
      startIndex,
      startIndex + rowsPerPage
    );
  }, [filteredData, page, rowsPerPage]);

  // =========================================================
  // KEEP PAGE VALID
  // =========================================================

  useEffect(() => {
    if (totalPages === 0) {
      if (page !== 1) {
        setPage(1);
      }
      return;
    }

    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  // =========================================================
  // RAILWAY NAME
  // =========================================================

  const getRailwayName = (railwayId) => {
    const railway = railways.find(
      (item) =>
        Number(item.id) === Number(railwayId)
    );

    return railway
      ? `${railway.code} - ${railway.name}`
      : "-";
  };

  // =========================================================
  // PAGINATION INFO
  // =========================================================

  const startRecord =
    filteredData.length === 0
      ? 0
      : (page - 1) * rowsPerPage + 1;

  const endRecord = Math.min(
    page * rowsPerPage,
    filteredData.length
  );

  // =========================================================
  // RESET FORM
  // =========================================================

  const resetForm = () => {
    setForm({
      code: "",
      name: "",
      railwayId: "",
      status: "Active",
    });

    setEditId(null);
  };

  // =========================================================
  // OPEN ADD
  // =========================================================

  const handleOpenAdd = () => {
    resetForm();
    setDialogOpen(true);
  };

  // =========================================================
  // EDIT
  // =========================================================

  const handleEdit = (item) => {
    setEditId(item.id);

    setForm({
      code: item.code || "",
      name: item.name || "",
      railwayId:
        item.railway_id ??
        item.railwayId ??
        "",
      status: item.status || "Active",
    });

    setDialogOpen(true);
  };

  // =========================================================
  // CLOSE DIALOG
  // =========================================================

  const handleCloseDialog = () => {
    if (loading) return;

    setDialogOpen(false);
    resetForm();
  };

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // SAVE
  // =========================================================

  const handleSave = async () => {
    if (!form.code.trim()) {
      return showMessage(
        "Code is required",
        "error"
      );
    }

    if (!form.name.trim()) {
      return showMessage(
        "Name is required",
        "error"
      );
    }

    if (!form.railwayId) {
      return showMessage(
        "Please select Railway",
        "error"
      );
    }

    try {
      setLoading(true);

      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        railway_id: Number(form.railwayId),
        status: form.status,
      };

      if (editId !== null) {
        const response = await api.put(
          `/divisions/${editId}`,
          payload
        );

        if (!response.data.success) {
          throw new Error(
            response.data.message ||
              "Division update failed"
          );
        }

        showMessage(
          "Division updated successfully"
        );
      } else {
        const response = await api.post(
          "/divisions",
          payload
        );

        if (!response.data.success) {
          throw new Error(
            response.data.message ||
              "Division creation failed"
          );
        }

        showMessage(
          "Division added successfully"
        );
      }

      await loadDivisions();

      setPage(1);

      setDialogOpen(false);

      resetForm();
    } catch (error) {
      console.error("Save error:", error);

      showMessage(
        error.response?.data?.message ||
          error.message ||
          "Unable to save record",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this Division?"
      )
    ) {
      return;
    }

    try {
      setLoading(true);

      const response = await api.delete(
        `/divisions/${id}`
      );

      if (!response.data.success) {
        throw new Error(
          response.data.message ||
            "Division deletion failed"
        );
      }

      await loadDivisions();

      showMessage(
        "Division deleted successfully"
      );
    } catch (error) {
      console.error("Delete error:", error);

      showMessage(
        error.response?.data?.message ||
          error.message ||
          "Unable to delete record",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // PAGE CHANGE
  // =========================================================

  const handlePageChange = (
    event,
    value
  ) => {
    setPage(value);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Box sx={{ width: "100%" }}>

      {/* =====================================================
          HEADER / SEARCH
      ===================================================== */}

      <Box
        sx={{
          p: 2,
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
        }}
      >
        <TextField
          size="small"
          placeholder="Search Division..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          sx={{
            width: {
              xs: "100%",
              sm: 350,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Chip
            label={`${filteredData.length} Records`}
            variant="outlined"
          />

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAdd}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              px: 2.5,
            }}
          >
            Add Division
          </Button>
        </Stack>
      </Box>

      {/* =====================================================
          DESKTOP
      ===================================================== */}

      {!isMobile ? (
        <>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              width: "100%",
              overflowX: "auto",
              borderTop:
                "1px solid #e2e8f0",
              borderBottom:
                "1px solid #e2e8f0",
            }}
          >
            <Table
              sx={{
                minWidth: 650,
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      "#f8fafc",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    #
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Code
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Division Name
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Railway
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Status
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedData.map(
                  (item, index) => (
                    <TableRow
                      key={item.id}
                      hover
                    >
                      <TableCell>
                        {(page - 1) *
                          rowsPerPage +
                          index +
                          1}
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={700}>
                          {item.code}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={600}>
                          {item.name}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {getRailwayName(
                          item.railway_id ??
                            item.railwayId
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={
                            item.status
                          }
                          size="small"
                          color={
                            item.status ===
                            "Active"
                              ? "success"
                              : "default"
                          }
                        />
                      </TableCell>

                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() =>
                            handleEdit(item)
                          }
                        >
                          <Edit />
                        </IconButton>

                        <IconButton
                          color="error"
                          onClick={() =>
                            handleDelete(
                              item.id
                            )
                          }
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                )}

                {filteredData.length ===
                  0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{ py: 5 }}
                    >
                      {loading
                        ? "Loading..."
                        : "No records found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* =================================================
              DESKTOP PAGINATION
          ================================================= */}

          {filteredData.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                  "space-between",
                gap: 2,
                px: 2,
                py: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Showing{" "}
                <strong>
                  {startRecord}
                </strong>{" "}
                -{" "}
                <strong>
                  {endRecord}
                </strong>{" "}
                of{" "}
                <strong>
                  {filteredData.length}
                </strong>{" "}
                records
              </Typography>

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={
                    handlePageChange
                  }
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              )}
            </Box>
          )}
        </>
      ) : (
        /* ===================================================
           MOBILE
        =================================================== */

        <>
          <Box sx={{ p: 1.5 }}>
            {paginatedData.map(
              (item, index) => (
                <Card
                  key={item.id}
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    border:
                      "1px solid #e2e8f0",
                    borderRadius: 2,
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent:
                          "space-between",
                        alignItems:
                          "flex-start",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          #
                          {(page - 1) *
                            rowsPerPage +
                            index +
                            1}
                        </Typography>

                        <Typography
                          fontWeight={800}
                          sx={{
                            mt: 0.3,
                          }}
                        >
                          {item.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Code: {item.code}
                        </Typography>
                      </Box>

                      <Chip
                        label={
                          item.status
                        }
                        size="small"
                        color={
                          item.status ===
                          "Active"
                            ? "success"
                            : "default"
                        }
                      />
                    </Box>

                    <Divider
                      sx={{ my: 1.5 }}
                    />

                    <Typography variant="body2">
                      <strong>
                        Railway:
                      </strong>{" "}
                      {getRailwayName(
                        item.railway_id ??
                          item.railwayId
                      )}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 1.5 }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Edit />}
                        onClick={() =>
                          handleEdit(item)
                        }
                      >
                        Edit
                      </Button>

                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={
                          <Delete />
                        }
                        onClick={() =>
                          handleDelete(
                            item.id
                          )
                        }
                      >
                        Delete
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )
            )}

            {filteredData.length ===
              0 && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 5,
                }}
              >
                <Typography color="text.secondary">
                  {loading
                    ? "Loading..."
                    : "No records found"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* =================================================
              MOBILE PAGINATION
          ================================================= */}

          {filteredData.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                alignItems: "center",
                justifyContent:
                  "center",
                gap: 1.5,
                px: 1,
                py: 2,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Showing{" "}
                <strong>
                  {startRecord}
                </strong>{" "}
                -{" "}
                <strong>
                  {endRecord}
                </strong>{" "}
                of{" "}
                <strong>
                  {filteredData.length}
                </strong>
              </Typography>

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={
                    handlePageChange
                  }
                  color="primary"
                  shape="rounded"
                  size="small"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              )}
            </Box>
          )}
        </>
      )}

      {/* =====================================================
          ADD / EDIT DIALOG
      ===================================================== */}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
          }}
        >
          {editId !== null
            ? "Edit Division"
            : "Add Division"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack
            spacing={2.2}
            sx={{ pt: 1 }}
          >
            <TextField
              fullWidth
              label="Division Code"
              name="code"
              value={form.code}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              label="Division Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />

            <TextField
              select
              fullWidth
              label="Railway"
              name="railwayId"
              value={form.railwayId}
              onChange={handleChange}
              required
            >
              <MenuItem value="">
                Select Railway
              </MenuItem>

              {railways
                .filter(
                  (railway) =>
                    railway.status ===
                    "Active"
                )
                .map((railway) => (
                  <MenuItem
                    key={railway.id}
                    value={railway.id}
                  >
                    {railway.code} -{" "}
                    {railway.name}
                  </MenuItem>
                ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="Status"
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <MenuItem value="Active">
                Active
              </MenuItem>

              <MenuItem value="Inactive">
                Inactive
              </MenuItem>
            </TextField>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            disabled={loading}
            sx={{
              textTransform: "none",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{
              textTransform: "none",
              px: 3,
            }}
          >
            {loading
              ? "Saving..."
              : editId !== null
              ? "Update"
              : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =====================================================
          SNACKBAR
      ===================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={
          handleSnackbarClose
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={
            handleSnackbarClose
          }
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Division;